import * as d3 from 'd3';
import './d3-distributionchart.css';
import chartTooltip from './d3-tooltip';
import { colors, DURATION, getSVGPosition } from '../utils';

export default function () {
  const margin = { top: 10, right: 20, bottom: 40, left: 65 };

  let width = 400;
  let height = 300;
  let svg;
  let tooltip;
  let _data;
  let duration;
  let chartWidth;
  let chartHeight;
  let x;
  let y;
  let xLabel;
  let yLabel;
  let assets;
  let dates;
  let selected;
  let statistic;

  const dispatch = d3.dispatch('selected');

  function chart(selection, isResize) {
    selection.each(function (data) {
      _data = data.filter(function (d) { return d.series === selected; });

      duration = isResize ? 0 : DURATION;

      chartWidth = Math.max(width - margin.left - margin.right, 0);
      chartHeight = Math.max(height - margin.top - margin.bottom, 0);

      createScales(data);

      if (!svg) {
        svg = selection.append('svg');
        svg.append('g');

        createGroups();
        createLabels();
        createGuideLines();
        createFocus();
        createTooltip();
      }

      svg.attr('width', width).attr('height', height);
      svg.select('g').attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');

      drawAxes();
      drawArea(0);
      drawDist();
      drawGuideLines();
      drawLabels();
      drawFocus();
    });
  }

  function createScales(data) {
    x = d3.scaleLinear()
      .rangeRound([0, chartWidth])
      .domain(d3.extent(data, function (d) { return d.index; }));

    y = d3.scaleLinear()
      .rangeRound([chartHeight, 0])
      .domain(d3.extent(_data, function (d) { return d.probability; }));
  }

  function createGroups() {
    svg.select('g')
      .append('g')
      .attr('class', 'pdf-chart__axis pdf-chart__axis--x');

    svg.select('g')
      .append('g')
      .attr('class', 'pdf-chart__axis pdf-chart__axis--y')

    svg.select('g')
      .append('g')
      .append('path')
      .attr('class', 'pdf-chart__area--left');

    svg.select('g')
      .append('g')
      .append('path')
      .attr('class', 'pdf-chart__area--right');

    svg.select('g')
      .append('g')
      .append('path')
      .attr('class', 'pdf-chart__line');
  }

  function createLabels() {
    xLabel = svg.select('g')
      .append('text')
      .attr('class', 'pdf-chart__label')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Index');

    yLabel = svg.select('g')
      .append('text')
      .attr('class', 'pdf-chart__label')
      .attr('dy', '0.71em')
      .attr('text-anchor', 'middle')
      .text('Probability');
  }

  function createGuideLines() {
    svg.select('g')
      .append('line')
      .attr('class', 'pdf-chart__baseline')
      .attr('x1', x(100))
      .attr('y1', 0)
      .attr('x2', x(100))
      .attr('y2', chartHeight)
      .attr('stroke', 'grey')
      .attr('stroke-width', 2)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');

    const returnStatisticGuideline = svg.select('g')
      .append('g')
      .attr('class', 'pdf-chart__statistic--return');

    returnStatisticGuideline.append('line')
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', 'grey')
      .attr('stroke-width', 2)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');

    returnStatisticGuideline.append('text')
      .attr('transform', 'rotate(-90)');

    const riskStatisticGuideline = svg.select('g')
      .append('g')
      .attr('class', 'pdf-chart__statistic--risk');

    riskStatisticGuideline.append('line')
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', 'grey')
      .attr('stroke-width', 2)
      .attr('opacity', 0.5)
      .attr('stroke-dasharray', '5,5');

    riskStatisticGuideline.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('text-anchor', 'end');
  }

  function createFocus() {
    svg.select('g')
      .append('rect')
      .attr('class', 'pdf-chart__focus--rect')
      .attr('fill', 'rgba(255, 255, 255, 0)')
      .on('mouseover', function () { focus.attr('display', 'block'); tooltip.show(); })
      .on('mouseout', function () { focus.attr('display', 'none'); tooltip.hide(); })
      .on('mousemove', mousemove);

    const focus = svg.select('g')
      .append('g')
      .attr('class', 'pdf-chart__focus')
      .attr('display', 'none')
      .attr('pointer-events', 'none');

    function mousemove() {
      const mouseXY = d3.mouse(this);
      const x0 = x.invert(mouseXY[0]);
      let i = d3.min([d3.max([d3.bisector(function (d) { return d.index; }).left(_data, x0), 1]), _data.length - 1]);
      const d0 = _data[i - 1];
      const d1 = _data[i];

      const right = x0 - d0.index > d1.index - x0;

      const d = right ? d1 : d0;
      i = right ? i : i - 1;

      const cumulativeProbability = _data.map(function (d) { return d.probability; })
        .filter(function (d, index) { return index < i; })
        .reduce(function (a, b) { return a + b; }, 0)
        + _data[i].probability / 2;

      focus.attr('transform', 'translate(' + x(d.index) + ', 0)');
      focus.select('circle')
        .attr('cy', y(d.probability));

      drawArea.call(this, i, true);

      const SVGPos = getSVGPosition(this);
      tooltip.update({ x: x(d.index) + SVGPos[0], y: mouseXY[1] + SVGPos[1] }, [], ('P(x < ' + d.index + ') = ' + d3.format('.2f')(cumulativeProbability) + '<br>P(x > ' + d.index + ') = ' + d3.format('.2f')((1 - cumulativeProbability))));
    }
  }

  function createTooltip() {
    tooltip = chartTooltip();
    const tooltipContainer = d3.select('body')
      .append('div')
      .attr('class', 'distribution-chart__tooltip');

    tooltipContainer.datum([]).call(tooltip);
  }

  function drawAxes() {
    svg.select('.pdf-chart__axis--x')
      .attr('transform', 'translate(0,' + chartHeight + ')')
      .transition()
      .duration(duration)
      .call(d3.axisBottom(x));

    svg.select('.pdf-chart__axis--y')
      .transition()
      .duration(duration)
      .call(d3.axisLeft(y));
  }

  function drawArea(maxIndex = Infinity, isInstant = false) {
    const area = d3.area()
      .curve(d3.curveLinear)
      .x(function (d) { return x(d.index); })
      .y1(function (d) { return y(d.probability); })
      .y0(y(0));

    const newDuration = isInstant ? 0 : duration;

    const left = svg.select('g')
      .select('.pdf-chart__area--left')
      .datum(_data.filter(function (d, i) { return i <= maxIndex; }));

    left.attr('fill', colors.red)
      .attr('opacity', 0.2)
      .transition()
      .duration(newDuration)
      .attr('d', area);

    const right = svg.select('g')
      .select('.pdf-chart__area--right')
      .datum(_data.filter(function (d, i) { return i >= maxIndex; }));

    right.attr('fill', colors.blue)
      .attr('opacity', 0.2)
      .transition()
      .duration(newDuration)
      .attr('d', area);
  }

  function drawDist() {
    const dist = svg.select('g')
      .select('.pdf-chart__line')
      .datum(_data);

    const line = d3.line()
      .curve(d3.curveLinear)
      .x(function (d) { return x(d.index); })
      .y(function (d) { return y(d.probability); });

    dist.attr('fill', 'none')
      .attr('stroke', 'black')
      .attr('stroke-width', '2px')
      .transition()
      .duration(duration)
      .attr('d', line);
  }

  function drawGuideLines() {
    svg.select('.pdf-chart__baseline')
      .transition()
      .duration(duration)
      .attr('x1', x(100))
      .attr('x2', x(100))
      .attr('y1', 0)
      .attr('y2', chartHeight);

    svg.select('.pdf-chart__statistic--return>line')
      .transition()
      .duration(duration)
      .attr('x1', x(statistic.returnStatistic.value))
      .attr('x2', x(statistic.returnStatistic.value))
      .attr('y1', 0)
      .attr('y2', chartHeight);

    svg.select('.pdf-chart__statistic--return>text')
      .transition()
      .duration(duration)
      .attr('x', 16 - chartHeight)
      .attr('y', x(statistic.returnStatistic.value) + 14)
      .text(statistic.returnStatistic.key);

    const displayRiskStatistic = statistic.riskStatistic.value !== 'NaN';
    const riskStatisticValue = displayRiskStatistic ? statistic.riskStatistic.value : 0;

    svg.select('.pdf-chart__statistic--risk>line')
      .classed('pdf-chart__statistic--risk-hidden', !displayRiskStatistic)
      .transition()
      .duration(duration)
      .attr('x1', x(riskStatisticValue))
      .attr('x2', x(riskStatisticValue))
      .attr('y1', 0)
      .attr('y2', chartHeight);

    svg.select('.pdf-chart__statistic--risk>text')
      .classed('pdf-chart__statistic--risk-hidden', !displayRiskStatistic)
      .transition()
      .duration(duration)
      .attr('x', -16)
      .attr('y', x(riskStatisticValue) + 14)
      .text(statistic.riskStatistic.key);

  }

  function drawLabels() {
    xLabel.attr('x', chartWidth / 2)
      .attr('y', chartHeight + 24);

    yLabel.attr('transform', 'translate(-55, ' + chartHeight / 2 + ')rotate(-90)');
  }

  function drawFocus() {
    svg.select('.pdf-chart__focus--rect')
      .attr('width', chartWidth)
      .attr('height', chartHeight);
  }

  chart.width = function (value) {
    if (!arguments.length) return width;
    width = value;
    return chart;
  }

  chart.height = function (value) {
    if (!arguments.length) return height;
    height = value;
    return chart;
  }

  chart.selected = function (value) {
    if (!arguments.length) return selected;
    selected = value;
    return chart;
  }

  chart.statistic = function (value) {
    if (!arguments.length) return statistic;
    statistic = value;
    return chart;
  }

  chart.assets = function (value) {
    if (!arguments.length) return assets;
    assets = value;
    return chart;
  }

  chart.dates = function (value) {
    if (!arguments.length) return dates;
    dates = value;
    return chart;
  }

  chart.on = function () {
    const value = dispatch.on.apply(dispatch, arguments);
    return value === dispatch ? chart : value;
  }

  chart.cleanup = function () {
    d3.select('.distribution-chart__tooltip').remove();
  }

  return chart;
}


// WEBPACK FOOTER //
// ./src/charts/d3/d3-distributionchart.js